/**
 * File Picker Hook
 * Main hook for file picker functionality (zero props pattern)
 */

import { useCallback } from "react";
import { useFilePickerStore } from "../store/file-picker-store";
import type { PickerConfig, SelectedFile } from "../types/file-picker-types";

export const useFilePicker = () => {
  const store = useFilePickerStore();

  // Open picker with configuration
  const openPicker = useCallback(
    (config: PickerConfig) => {
      store.openPicker(config);
    },
    [store]
  );

  // Close picker
  const closePicker = useCallback(() => {
    store.closePicker("cancel");
  }, [store]);

  // Select files and close (for programmatic usage)
  const selectFiles = useCallback(
    (files: SelectedFile[]) => {
      const { config } = store;

      // Clear current selection and add new files
      store.clearSelection();
      files.forEach((file) => store.selectFile(file));

      // Call onSelect callback if provided
      if (config?.onSelect) {
        config.onSelect(files);
      }

      // Close picker
      store.closePicker("confirm");
    },
    [store]
  );

  // Quick open methods for common use cases
  const openImagePicker = useCallback(
    (config: Omit<PickerConfig, "allowedTypes"> = {}) => {
      openPicker({
        ...config,
        allowedTypes: ["image/*"],
        title: config.title || "Görsel Seç",
      });
    },
    [openPicker]
  );

  const openVideoPicker = useCallback(
    (config: Omit<PickerConfig, "allowedTypes"> = {}) => {
      openPicker({
        ...config,
        allowedTypes: ["video/*"],
        title: config.title || "Video Seç",
      });
    },
    [openPicker]
  );

  const openAudioPicker = useCallback(
    (config: Omit<PickerConfig, "allowedTypes"> = {}) => {
      openPicker({
        ...config,
        allowedTypes: ["audio/*"],
        title: config.title || "Ses Dosyası Seç",
      });
    },
    [openPicker]
  );

  const openDocumentPicker = useCallback(
    (config: Omit<PickerConfig, "allowedTypes"> = {}) => {
      openPicker({
        ...config,
        allowedTypes: [
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "text/plain",
        ],
        title: config.title || "Belge Seç",
      });
    },
    [openPicker]
  );

  const openMultipleImagePicker = useCallback(
    (config: Omit<PickerConfig, "allowedTypes"> = {}) => {
      openPicker({
        ...config,
        allowedTypes: ["image/*"],
        maxSelection: config.maxSelection || 10,
        title: config.title || "Görseller Seç",
      });
    },
    [openPicker]
  );

  return {
    // State
    isOpen: store.isOpen,
    selectedFiles: store.selectedFiles,
    source: store.source,
    config: store.config,
    isLoading: store.isLoading,
    isUploading: store.isUploading,
    isGoogleAuthed: store.isGoogleAuthed,

    // Actions
    openPicker,
    closePicker,
    selectFiles,

    // Quick open methods
    openImagePicker,
    openVideoPicker,
    openAudioPicker,
    openDocumentPicker,
    openMultipleImagePicker,

    // Source management
    setSource: store.setSource,

    // File management
    selectFile: store.selectFile,
    removeFile: store.removeFile,
    clearSelection: store.clearSelection,

    // Google Drive
    setGoogleAuth: store.setGoogleAuth,

    // Loading states
    setLoading: store.setLoading,
    setUploading: store.setUploading,

    // Reset
    reset: store.reset,
  };
};
